import { z } from "zod";

/**
 * `bot_versions.config`, schema version 1. Agency-internal — never sent to
 * the widget (see PLAN.md "`theme` is its own column, not a key inside
 * `config`" and "`session.ready` carries the theme, never the prompt or
 * tool config").
 */
export const botConfigV1Schema = z.object({
  prompt: z.string().min(1),
  greeting: z.string().optional(),
  modelParameters: z
    .object({
      temperature: z.number().min(0).max(2).default(1),
      maxTokens: z.number().int().positive().optional(),
    })
    .default({}),
  tools: z.array(z.string()).default([]),
  /** Hostnames `present_actions`'s `open_url` intent may target — a dedicated allowlist, deliberately not derived from `bot_keys.allowedOrigins` (that list governs where the widget may *embed*, a different concern from where it may *link*). */
  allowedActionDomains: z.array(z.string()).default([]),
  proactiveRules: z
    .object({
      afterSeconds: z.number().int().positive().optional(),
      onUrlMatch: z.string().optional(),
      onExitIntent: z.boolean().optional(),
      maxPerSession: z.number().int().positive().optional(),
      onlyIfNeverOpened: z.boolean().optional(),
    })
    .default({}),
  /** Debounced AI-profile regeneration threshold — PLAN.md "Why 20 and not 10". */
  profileWindow: z.number().int().positive().default(20),
  /**
   * Whether visitors may attach images. Off by default and per bot,
   * because it is three decisions at once and none of them is universal:
   * whether this bot's model can see, whether the client wants shopper
   * photos in their storage at all, and whether a vision turn's cost is
   * one they want to carry. Defaulting it on would make all three for them.
   *
   * Enforced server-side on upload as well as hidden in the widget — the
   * widget's copy of this is a UI hint, not the gate.
   */
  allowImageUploads: z.boolean().default(false),
});

export type BotConfigV1 = z.infer<typeof botConfigV1Schema>;
